import React, {FunctionComponent, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../redux/slices/dialogs/actions";
import {Box, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {BizFormApiClient, BizFormEditForm} from "../../../packages/biz-form";
import {useBizFormApiResolver} from "../../hooks/use-biz-form-api-resolver";
import Dialog from "../../components/base/dialog";
import Button from "../../components/base/button/button";

const EditBizFormDialog: FunctionComponent = () => {
    const dispatch = useDispatch();
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.editBizForm] ?? {});
    const apiClient = useBizFormApiResolver(state?.formApiResolver ?? ((t) => t.operatorApp)) as BizFormApiClient;

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(async () => {
        if (state.onCloseCallback) {
            await state.onCloseCallback();
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.editBizForm, {open: false}));
    }, [dispatch, state])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick)
                    closeDialog().then();
                break;
            case "closeClick":
                if (!state.hideCloseButton)
                    closeDialog().then();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown)
                    closeDialog().then();
                break;
            default:
                break;
        }
    }, [closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])

    /**
     * Manages the remove confirmation dialog.
     */
    const removeConfirmationDialog = useCallback((open, options = {}) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {open: open, ...(options ?? {})}));
    }, [dispatch])

    return (
        <>
            <Dialog
                {...dialogProps}
                maxWidth="md"
                fullWidth
                setOpen={() => onDialogClosed({}, 'closeClick')}
                onClose={onDialogClosed}
                scroll={'paper'}
            >
                <DialogTitle fontWeight={600} sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme => theme.palette.background.paper,
                    zIndex: 199,
                }}>
                    View and Edit Form
                </DialogTitle>
                <DialogContent>
                    <Box sx={{paddingBlockStart: 2, paddingBlockEnd: 4, display: 'flex', flexDirection: 'column', gap: 4}}>
                        <Typography variant="body2" fontWeight={600}>
                            you can view and edit the form here.
                        </Typography>
                        <BizFormEditForm
                            apiClient={apiClient}
                            formId={state.formReference}
                            removeConfirmationDialog={removeConfirmationDialog}
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 0,
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme => theme.palette.background.paper,
                        zIndex: 199,
                        gap: 2,
                        borderTop: '1px solid',
                        borderColor: theme => theme.palette.divider
                    }}
                >
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={(e) => onDialogClosed(e, 'closeClick')}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EditBizFormDialog;
